import { useCallback, useEffect, useRef, useState } from 'react'
import {
  StoreLocationConfig,
  StoreMapFilterSubGroupOption
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  createStoresSDK,
  getStoreClusters,
  type StoreClustersParams
} from '../../../../../services/stores/stores-sdk'
import { radiusByZoom, transformStoreLocationsData } from '../utils'
import { CbxConfigType } from '../../../../../utils/page-config'
import { StoreImage, StoreLocatorConfig } from '../types'
import type { PaymentMethodConfig } from '../../../StoreDetails/types'

interface ServicesListAndStoreTypeMemoPropsTypes {
  servicesList: string[]
  storeInfoType: string[]
  selectedOpening: string[]
}

interface UseStoreClustersParams {
  zoom: number
  setZoom: (zoom: number) => void
  setHasUserZoomed: (hasUserZoomed: boolean) => void
  cbxConfig: CbxConfigType
  searchLocationLatitude: number
  searchLocationLongitude: number
  platformBrands?: string
  storeInfoUse?: string
  lang: string
  storeImage?: StoreImage
  storeLocatorConfig?: StoreLocatorConfig
  storeDetailsConfig: {
    paymentMethodsTitle: string
    payments: Array<PaymentMethodConfig>
  }
  servicesListAndStoreTypeMemo?: ServicesListAndStoreTypeMemoPropsTypes
  storeTypeOrder?: string
  enableMilesAsDistanceUnit?: boolean
  isSsr: boolean
  isUserLoggedIn: boolean
  storeLandingPagePath?: string
  isAuthor: boolean
  hasUserZoomed: boolean
  radiusLimit?: number
  isAutomaticZoomOut: boolean
  setIsAutomaticZoomOut: (value: boolean) => void
  googleMapsReady: boolean
}

interface UseStoreClustersReturn {
  storeLocations: StoreLocationConfig[] | undefined
  isLoadingStores: boolean
  initialStoreCount: number | null
  fetchStoreClusters: (
    additionalParams?: Record<string, unknown> | StoreMapFilterSubGroupOption[]
  ) => Promise<void>
  resetStoreState: () => void
}

export const useStoreClusters = (
  params: UseStoreClustersParams
): UseStoreClustersReturn => {
  const {
    zoom,
    cbxConfig,
    searchLocationLatitude,
    searchLocationLongitude,
    platformBrands,
    servicesListAndStoreTypeMemo,
    storeInfoUse,
    lang,
    storeImage,
    storeLocatorConfig,
    storeDetailsConfig,
    storeTypeOrder,
    enableMilesAsDistanceUnit,
    isSsr,
    isUserLoggedIn,
    storeLandingPagePath,
    isAuthor,
    hasUserZoomed,
    isAutomaticZoomOut,
    setIsAutomaticZoomOut,
    setHasUserZoomed,
    setZoom,
    radiusLimit,
    googleMapsReady
  } = params

  const [storeLocations, setStoreLocations] = useState<StoreLocationConfig[]>()
  const [isLoadingStores, setIsLoadingStores] = useState(false)
  const [initialStoreCount, setInitialStoreCount] = useState<number | null>(
    null
  )
  const hasInitialFetchRef = useRef(false)
  const previousZoomRef = useRef(zoom)

  const automaticZoomOut = () => {
    setIsAutomaticZoomOut(true)
    setHasUserZoomed(false)
    const newZoomOut = zoom - 1
    const zoomOutValue = radiusByZoom[newZoomOut] ?? 0
    const hasReachedZoomBoundary =
      zoomOutValue == null || !radiusLimit || zoomOutValue > radiusLimit
    if (hasReachedZoomBoundary) {
      setIsAutomaticZoomOut(false)
    } else {
      setZoom(newZoomOut)
    }
  }
  const fetchStoreClusters = useCallback(
    async (additionalParams = {}) => {
      setIsLoadingStores(true)
      try {
        const {
          cbxUrl: baseCbxUrl,
          market: cbxMarket,
          applicationId,
          loggerEnabled
        } = cbxConfig

        const apiParams: StoreClustersParams = {
          distance: radiusByZoom[zoom] ?? 0.1,
          latitude: searchLocationLatitude,
          longitude: searchLocationLongitude,
          ...(platformBrands && {
            platformBrands: platformBrands.split(',')
          }),
          ...(servicesListAndStoreTypeMemo &&
            servicesListAndStoreTypeMemo?.servicesList?.length > 0 && {
              servicesList: servicesListAndStoreTypeMemo?.servicesList
            }),
          ...(servicesListAndStoreTypeMemo &&
            servicesListAndStoreTypeMemo?.storeInfoType?.length > 0 && {
              storeInfoType: servicesListAndStoreTypeMemo?.storeInfoType
            }),
          ...(storeInfoUse && {
            storeInfoUse: storeInfoUse.split(',')
          }),
          ...additionalParams
        }

        const response = await getStoreClusters(
          createStoresSDK({
            baseCbxUrl,
            market: cbxMarket,
            lang,
            applicationId,
            loggerEnabled
          }),
          apiParams
        )

        if (
          response &&
          'storeClusters' in response &&
          Array.isArray(response.storeClusters) &&
          response.storeClusters.length > 0
        ) {
          setIsAutomaticZoomOut(false)
          const selectedOpening =
            'selectedOpening' in additionalParams
              ? (additionalParams.selectedOpening as string[])
              : (servicesListAndStoreTypeMemo?.selectedOpening ?? [])

          const mappedStores = await transformStoreLocationsData({
            storeLocations: response?.storeClusters,
            storeImage: storeImage!,
            setZoom,
            storeLocatorConfig,
            storeDetailsConfig,
            userLocation: {
              latitude: searchLocationLatitude,
              longitude: searchLocationLongitude
            },
            storeTypeOrder,
            enableMilesAsDistanceUnit,
            isSsr,
            isUserLoggedIn,
            selectedOpening
          })
          setIsAutomaticZoomOut(false)
          setStoreLocations(mappedStores)
          if (initialStoreCount === null) {
            setInitialStoreCount(mappedStores.length)
          }
        } else {
          setStoreLocations([])
          if (!hasUserZoomed) {
            automaticZoomOut()
          }

          if (initialStoreCount === null) {
            setInitialStoreCount(0)
          }
        }
      } finally {
        setIsLoadingStores(false)
      }
    },
    [
      zoom,
      cbxConfig,
      searchLocationLatitude,
      searchLocationLongitude,
      platformBrands,
      lang,
      storeImage,
      storeLocatorConfig,
      storeDetailsConfig,
      storeTypeOrder,
      enableMilesAsDistanceUnit,
      isSsr,
      isUserLoggedIn,
      initialStoreCount
    ]
  )

  const resetStoreState = useCallback(() => {
    setInitialStoreCount(null)
    setIsLoadingStores(false)
    hasInitialFetchRef.current = false
  }, [])

  useEffect(() => {
    const hasValidLocation = searchLocationLatitude && searchLocationLongitude
    const hasAlreadyFetched = hasInitialFetchRef.current

    if (hasValidLocation && !hasAlreadyFetched && googleMapsReady) {
      hasInitialFetchRef.current = true
      fetchStoreClusters()
      return
    }

    if (!hasValidLocation && storeLandingPagePath && !isAuthor) {
      window.location.href = storeLandingPagePath
    }
  }, [
    searchLocationLatitude,
    searchLocationLongitude,
    storeLandingPagePath,
    isAuthor,
    fetchStoreClusters,
    googleMapsReady
  ])

  useEffect(() => {
    const hasValidLocation = searchLocationLatitude && searchLocationLongitude
    const shouldFetch = hasInitialFetchRef.current && hasValidLocation

    if (!shouldFetch) {
      return
    }

    const hasZoomChanged = zoom !== previousZoomRef.current
    const shouldFetchOnZoomChange =
      (hasUserZoomed && !isAutomaticZoomOut) ||
      (!hasUserZoomed && isAutomaticZoomOut) ||
      (!hasUserZoomed && !isAutomaticZoomOut && hasZoomChanged)

    if (shouldFetchOnZoomChange) {
      fetchStoreClusters()
    }
    previousZoomRef.current = zoom
  }, [
    zoom,
    isAutomaticZoomOut,
    hasUserZoomed,
    searchLocationLatitude,
    searchLocationLongitude,
    fetchStoreClusters
  ])

  return {
    storeLocations,
    isLoadingStores,
    initialStoreCount,
    fetchStoreClusters,
    resetStoreState
  }
}
